import React from 'react';
import {
  ToastContainer,
  toast as toastLib,
  ToastOptions,
  ToastPromiseParams,
} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export type ToastPosition =
  | 'top-left'
  | 'top-right'
  | 'top-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'default';

export interface ToasterProps {
  /** Position of toasts on screen. Default: top-right */
  position?: ToastPosition;
  /** Auto close delay in ms. Set to false to disable. Default: 5000 */
  autoClose?: number | false;
  /** Show newest toast on top. Default: true */
  newestOnTop?: boolean;
  /** Limit number of toasts visible. Default: 3 */
  limit?: number;
  /** Hide progress bar. Default: false */
  hideProgressBar?: boolean;
  /** Pause toast timer on hover. Default: true */
  pauseOnHover?: boolean;
  /** Theme: light, dark, or colored. Default: light */
  theme?: 'light' | 'dark' | 'colored';
  /** Close toast on click. Default: true */
  closeOnClick?: boolean;
  /** Enable RTL layout. Default: false */
  rtl?: boolean;
  /** Optional container id when using multiple containers */
  containerId?: string | number;
}

const defaultProps: ToasterProps = {
  position: 'top-right',
  autoClose: 5000,
  newestOnTop: true,
  limit: 3,
  hideProgressBar: false,
  pauseOnHover: true,
  theme: 'light',
  closeOnClick: true,
  rtl: false,
};

export function Toaster(props: ToasterProps = {}) {
  const {
    position,
    autoClose,
    newestOnTop,
    limit,
    hideProgressBar,
    pauseOnHover,
    theme,
    closeOnClick,
    rtl,
    containerId,
  } = { ...defaultProps, ...props };

  return (
    <ToastContainer
      position={position}
      autoClose={autoClose}
      newestOnTop={newestOnTop}
      limit={limit}
      hideProgressBar={hideProgressBar}
      pauseOnHover={pauseOnHover}
      theme={theme}
      closeOnClick={closeOnClick}
      rtl={rtl}
      containerId={containerId}
    />
  );
}

const defaultToastOptions: ToastOptions = {
  position: defaultProps.position,
  autoClose: defaultProps.autoClose,
};

/**
 * Toast API for use anywhere in the app.
 *
 * @example
 *
 * toast.success('Saved successfully');
 * toast.error('Something went wrong');
 * toast.info('Tip: You can undo this.');
 * toast.warning('Please check your input.');
 * toast('Custom message');
 *
 * // With options
 * toast.success('Done!', { autoClose: 3000 });
 * toast.error('Error', { position: 'bottom-center' });
 */
export const toast = {
  success: (message: React.ReactNode, options?: ToastOptions) =>
    toastLib.success(message, { ...defaultToastOptions, ...options }),

  error: (message: React.ReactNode, options?: ToastOptions) =>
    toastLib.error(message, { ...defaultToastOptions, ...options }),

  info: (message: React.ReactNode, options?: ToastOptions) =>
    toastLib.info(message, { ...defaultToastOptions, ...options }),

  warning: (message: React.ReactNode, options?: ToastOptions) =>
    toastLib.warning(message, { ...defaultToastOptions, ...options }),

  /** Default toast (no type icon) */
  default: (message: React.ReactNode, options?: ToastOptions) =>
    toastLib(message, { ...defaultToastOptions, ...options }),

  /** Dismiss a toast by id */
  dismiss: (id?: string | number) => toastLib.dismiss(id),

  /** Promise-based: show success on resolve, error on reject */
  promise: <T, E = unknown, D = unknown>(
    promise: Promise<T>,
    messages: ToastPromiseParams<T, E, D>,
    options?: ToastOptions
  ) =>
    toastLib.promise(promise, messages as ToastPromiseParams<unknown, unknown, unknown>, options),
};

export default Toaster;
